import { jsx as _jsx } from "react/jsx-runtime";
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthScreen from 'components/AuthScreen';
import { recordVerificationEmailSent, useFetchSignInMethods, useRequestPasswordReset, useSignIn, useSignUp, } from 'features/auth';
function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
export default function LoginScreen() {
    const { t } = useTranslation();
    const [mode, setMode] = useState('login');
    const [step, setStep] = useState('email');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [formMessage, setFormMessage] = useState('');
    const [globalMessage, setGlobalMessage] = useState('');
    const fetchMethods = useFetchSignInMethods();
    const signIn = useSignIn();
    const signUp = useSignUp();
    const requestReset = useRequestPasswordReset();
    const isSubmitting = fetchMethods.isPending || signIn.isPending || signUp.isPending || requestReset.isPending;
    const clearFormMessage = () => {
        if (formMessage)
            setFormMessage('');
    };
    const handleSubmit = async () => {
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail) {
            setFormMessage(mode === 'reset' ? t('reset_missing_email') : t('auth_email_required'));
            return;
        }
        if (!isValidEmail(normalizedEmail)) {
            setFormMessage(t('auth_invalid_email'));
            return;
        }
        if (mode === 'reset') {
            setFormMessage('');
            try {
                await requestReset.mutateAsync({ email: normalizedEmail });
                setGlobalMessage(t('reset_email_sent'));
            }
            catch (error) {
                const err = error;
                setFormMessage(t('reset_email_failed', { message: err?.message || 'unknown error' }));
            }
            return;
        }
        if (step === 'email') {
            setFormMessage('');
            try {
                const methods = await fetchMethods.mutateAsync({ email: normalizedEmail });
                setPassword('');
                setFullName('');
                setStep(methods.length > 0 ? 'password' : 'signup');
            }
            catch {
                setFormMessage(t('auth_invalid_email'));
            }
            return;
        }
        if (step === 'password') {
            if (!password.trim()) {
                setFormMessage(t('auth_password_required'));
                return;
            }
            try {
                await signIn.mutateAsync({ email: normalizedEmail, password });
                setGlobalMessage('');
            }
            catch (error) {
                const err = error;
                const message = err?.code === 'auth/wrong-password' ||
                    err?.code === 'auth/invalid-credential' ||
                    err?.code === 'auth/invalid-email' ||
                    err?.code === 'auth/user-not-found'
                    ? t('auth_wrong_password')
                    : t('login_failed', { message: err?.message || 'unknown error' });
                setFormMessage(message);
            }
            return;
        }
        if (step === 'signup') {
            const name = fullName.trim();
            if (!name) {
                setFormMessage(t('auth_name_required'));
                return;
            }
            if (password.trim().length < 6) {
                setFormMessage(t('auth_password_too_short'));
                return;
            }
            try {
                await signUp.mutateAsync({ email: normalizedEmail, password, displayName: name });
                recordVerificationEmailSent(normalizedEmail);
                setGlobalMessage(t('auth_verification_email_sent'));
                setStep('email');
                setPassword('');
                setFullName('');
            }
            catch (error) {
                const err = error;
                if (err?.code === 'auth/email-already-in-use') {
                    setStep('password');
                    setFormMessage(t('auth_email_already_registered'));
                    return;
                }
                setFormMessage(t('auth_signup_failed', { message: err?.message || 'unknown error' }));
            }
        }
    };
    return (_jsx(AuthScreen, { email: email, formMessage: formMessage, globalMessage: globalMessage, isSubmitting: isSubmitting, mode: mode, step: step, fullName: fullName, onEmailChange: (value) => {
            setEmail(value);
            clearFormMessage();
        }, onModeChange: (nextMode) => {
            setMode(nextMode);
            setFormMessage('');
            setGlobalMessage('');
            if (nextMode === 'login') {
                // Returning to sign-in after a reset request — drop back to the password step
                // if the user had already identified themselves, otherwise to email.
                setPassword('');
                setFullName('');
            }
        }, onBackToEmail: () => {
            setStep('email');
            setPassword('');
            setFullName('');
            setFormMessage('');
        }, onPasswordChange: (value) => {
            setPassword(value);
            clearFormMessage();
        }, onFullNameChange: (value) => {
            setFullName(value);
            clearFormMessage();
        }, onSubmit: handleSubmit, password: password }));
}
